import { useCallback, useState, useEffect, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Record, useVersion } from 'react-admin';
import { Box, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { SnackbarContainer } from 'components/standalone';
import CustomSelect from 'components/CustomSelect';
import { FormToolbar } from 'components/standalone';
import { extractErrorMessage } from 'utils';
import { UserContainer, UserFormValues } from '../containers';
import { UserForm } from '../components';

type LocationState = {
  formState: Record;
  selectedBumpers: string[];
};

const UserEditInner = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { notify } = SnackbarContainer.useContainer();
  const {
    selectedUser,
    isLoading,
    userLoaded,
    userError,
    formLoading,
    supervisors,
    supervisorRoleId,
    currentUserRegion,
    userRoles,
    userRegions,
    userRolesLoaded,
    userRegionsLoaded,
    linksError,
    loadUserById,
    updateUser,
    loadRoleOptions,
    loadRegionOptions,
    findInactiveRegion,
    loadSupervisorRoleId,
    loadUserRegion,
    loadUserRolesForUser,
    loadUserRegionsForUser,
    loadSupervisorsForRegion,
    updateUserRegionManager,
    setFormLoading,
    resetLoadedFlags,
  } = UserContainer.useContainer();

  // BACKWARDS COMPAT: useVersion triggers re-fetch when react-admin
  // toolbar refresh button is clicked. Required while using custom
  // containers instead of react-admin's built-in data hooks.
  const version = useVersion();

  const [regionalManagerOpen, setRegionalManagerOpen] = useState(false);
  const [manager, setManager] = useState<string | undefined>();
  // Track current role IDs for supervisor check (updated via onRolesChange)
  const [currentRoleIds, setCurrentRoleIds] = useState<string[]>([]);

  const searchParams = new URLSearchParams(location.search);
  const contractorId = searchParams.get('contractorId');

  const routerState = location.state as LocationState | undefined;
  const formState = routerState?.formState || null;
  const selectedBumpers = routerState?.selectedBumpers || null;

  const userId = selectedUser?.id;
  const userRegionId = currentUserRegion?.regionId;
  const userManagerId = currentUserRegion?.managerUserId;

  useEffect(() => {
    if (!id) return;
    loadUserById(id);
  }, [id, version, loadUserById]);

  // Load secondary data when user id changes
  useEffect(() => {
    if (!userId) return;
    resetLoadedFlags();
    loadSupervisorRoleId();
    loadUserRegion(userId);
    loadUserRolesForUser(userId);
    loadUserRegionsForUser(userId);
  }, [
    userId,
    loadSupervisorRoleId,
    loadUserRegion,
    loadUserRolesForUser,
    loadUserRegionsForUser,
    resetLoadedFlags,
  ]);

  useEffect(() => {
    if (userError || (userLoaded && !selectedUser)) {
      notify('User not found', 'error');
      history.push('/user');
    }
  }, [userError, userLoaded, selectedUser, notify, history]);

  useEffect(() => {
    if (linksError) notify(linksError, 'error');
  }, [linksError, notify]);

  // Set manager when userRegion manager changes
  useEffect(() => {
    if (userManagerId) {
      setManager(userManagerId);
    }
  }, [userManagerId]);

  // Load supervisors when userRegion regionId changes
  useEffect(() => {
    if (!userRegionId) return;
    loadSupervisorsForRegion(userRegionId);
  }, [userRegionId, loadSupervisorsForRegion]);

  // Check if user has Supervisor role
  const hasSupervisorRole = useMemo(() => {
    if (!supervisorRoleId) return false;
    return currentRoleIds.includes(supervisorRoleId);
  }, [currentRoleIds, supervisorRoleId]);

  const initialRoleIds = useMemo(
    () => userRoles.map(ur => ur.roleId),
    [userRoles],
  );
  const initialRegionIds = useMemo(
    () => userRegions.map(ur => ur.regionId),
    [userRegions],
  );

  // Get regional manager name
  const regionalManagerName = useMemo(() => {
    if (!supervisors.length || !manager) return '';
    const regionalManager = supervisors.find(s => s.id === manager);
    return regionalManager?.name || '';
  }, [supervisors, manager]);

  const handleSubmit = useCallback(
    async (values: UserFormValues) => {
      if (!selectedUser) return;

      const successMessage =
        values.active === false && selectedUser.active === true
          ? 'User updated and moved to inactive region.'
          : 'Element updated';

      try {
        // Single transactional request: user fields + desired role and
        // region sets. The response already tells whether the user is a
        // Supervisor, so no read-back is needed.
        const updated = await updateUser(id, values);

        if (updated.isSupervisor) {
          await loadUserRegion(id);
          setRegionalManagerOpen(true);
          return;
        }

        // 5. Otherwise redirect based on contractorId
        if (!contractorId) {
          history.push('/user');
          notify(successMessage);
          return;
        }

        // Redirect to contractor's edit page if from contractor flow
        history.push({
          pathname: `/contractor/${contractorId}`,
          state: {
            _scrollToTop: true,
            user: selectedUser,
            formState,
            selectedBumpers,
          },
        });
      } catch (error: unknown) {
        // Nothing was persisted (single transaction), so the operator's
        // values stay on screen for a retry.
        console.error('User update failed:', error);
        notify(
          extractErrorMessage(error, 'Error while updating the user'),
          'error',
        );
      }
    },
    [
      id,
      selectedUser,
      updateUser,
      loadUserRegion,
      contractorId,
      formState,
      selectedBumpers,
      history,
      notify,
    ],
  );

  const handleSaveRegionalManager = useCallback(async () => {
    if (!currentUserRegion || !manager) return;

    setFormLoading(true);
    try {
      await updateUserRegionManager(currentUserRegion.id, manager);
      history.push('/user');
      notify('Element updated');
    } catch (error: any) {
      console.error('Failed to update regional manager:', error);
      notify(`User update error: ${error.message}`, 'error');
    } finally {
      setFormLoading(false);
    }
  }, [
    currentUserRegion,
    manager,
    updateUserRegionManager,
    history,
    notify,
    setFormLoading,
  ]);

  const handleGoBack = useCallback(() => {
    history.push('/user');
  }, [history]);

  if (isLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedUser) {
    // Either still loading or "not found" redirect pending
    return null;
  }

  if (regionalManagerOpen) {
    return (
      <Box>
        <Box className={classes.formContainer}>
          <Box className={classes.formContent}>
            <CustomSelect
              label="Regional Manager"
              resource="regionalManager"
              options={supervisors}
              value={manager}
              onChange={event => {
                if (event.target.value === manager) return;
                setManager(event.target.value as string);
              }}
            />
          </Box>
          <FormToolbar
            onGoBack={handleGoBack}
            onSave={handleSaveRegionalManager}
            saveLoading={formLoading}
            showSave={true}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box className={classes.formContainer}>
        <UserForm
          mode="edit"
          initialValues={selectedUser}
          onSubmit={handleSubmit}
          // Saving with a half-loaded role/region list would send an
          // incomplete desired set and drop relations, so the form stays
          // disabled until both lists are in.
          loading={formLoading || !userRolesLoaded || !userRegionsLoaded}
          onGoBack={handleGoBack}
          loadRoleOptions={loadRoleOptions}
          loadRegionOptions={loadRegionOptions}
          initialRoleIds={initialRoleIds}
          initialRegionIds={initialRegionIds}
          onRolesChange={setCurrentRoleIds}
          regionalManagerName={regionalManagerName}
          showRegionalManager={hasSupervisorRole}
          findInactiveRegion={findInactiveRegion}
        />
      </Box>
    </Box>
  );
};

export const UserEdit = () => {
  return (
    <UserContainer.Provider>
      <UserEditInner />
    </UserContainer.Provider>
  );
};

const useStyles = makeStyles(theme => ({
  formContainer: {
    position: 'relative',
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.background.paper,
  },
  formContent: {
    padding: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
}));

export default UserEdit;
